/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { Tabs, message, Result, Button, Modal, Tooltip } from 'antd';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import { sortByNumberProp } from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { styleFn } from './FileTable.style';
import { FileTableProps } from './FileTable';
import { queryCache, useMutation, useQuery } from 'react-query';
import {
  claimSharedTender,
  getShareStatus,
  LoadShareParams,
  loadTenderShareCoreRegistration,
  validateShareKey,
  rejectShareKey,
} from '~src/services/api/shares';
import RightSideContentWrapper from '../Wrappers/RightSideContentWrapper/RightSideContentWrapper';
import TenderDetails from '../../molecules/DetailsLayouts/TenderDetails';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { TendersDocumentsTableUrlParams } from './StructuredFoldersTendersDocuemntsTable';
import TenderClaimModal from '~components/popups-modals/CachedSharesModals/TenderClaimModals/TenderClaim.modal';
import { appLog } from '~src/utils/log';
import { DB_STATE } from '~src/types/enums';
import { formatDate } from '~src/utils/formatDate';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import RejectShareKeyModal from '~components/popups-modals/CachedSharesModals/RejectShareKeyModal/RejectShareKey.modal';

const { TabPane } = Tabs;

export type StoredSharedTender = {
  title: string;
  shareId?: string;
  shareKey: string | null;
};

const sortByUpdatedAt = sortByNumberProp('updatedAt');

// URL params for both:
// - /invitations/:tenderId
// - /share-keys/:slug/:compoundId (where compoundId = "{shareId}-{shareKey}")
type SharedTenderUrlParams = TendersDocumentsTableUrlParams & {
  slug?: string;
  compoundId?: string;
};

/** Public shared tender view (cached share) */
export const SharedTenderAttachmentsTable: React.FC<
  FileTableProps & { resetSelectedForDetails: () => void }
> = ({
  highlightedRow,
  setSelectedItems,
  handleRow,
  resetSelectedForDetails,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const { path, url } = useRouteMatch();

  const [isSharedTenderClaimVisible, setIsSharedTenderVisible] = useState(
    false
  );
  const [activeTab, setActiveTab] = useState<string | undefined>();
  const [tenderClaimModalVisible, setTenderClaimModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [fromRemoveFlow, setFromRemoveFlow] = useState(false);

  const {
    cachedSharesContext,
    setCachedSharesContext,
    currentCachedShare,
    setCurrentCachedShare,
  } = useContext(CachedSharesContext);

  const {
    tenderId: tenderIdParam,
    currentFolderId,
    rootFolderId,
    slug,
    compoundId,
  } = useParams<SharedTenderUrlParams>();

  const parseShareIdKey = (value?: string) => {
    if (!value) {
      return {
        shareId: undefined as string | undefined,
        shareKey: undefined as string | undefined,
      };
    }

    const parts = value.split('-');

    if (parts.length < 2) {
      return {
        shareId: value,
        shareKey: undefined,
      };
    }

    return {
      shareId: parts[0],
      shareKey: parts.slice(1).join('-'),
    };
  };

  // /share-keys/:slug/:compoundId
  const {
    shareId: shareIdFromCompound,
    shareKey: shareKeyFromCompound,
  } = parseShareIdKey(compoundId);

  // /invitations/:tenderId  where tenderId can now also be "{shareId}-{shareKey}"
  const {
    shareId: shareIdFromInvitationParam,
    shareKey: shareKeyFromInvitationParam,
  } = parseShareIdKey(tenderIdParam);

  // Unified resolved share id/key
  const tenderId =
    shareIdFromInvitationParam || shareIdFromCompound || tenderIdParam;

  const currentTenderKey =
    currentCachedShare?.shareId === tenderId
      ? currentCachedShare.shareKey
      : undefined;

  const shareKeyFromUrl =
    shareKeyFromInvitationParam || shareKeyFromCompound || undefined;

  // Invitations must always carry shareKey in URL.
  // Do not fall back to context for old /invitations/:shareId links.
  const shareKeyToValidate = shareKeyFromUrl;
  const hasShareKey = !!shareKeyFromUrl;

  const shareKeyValidationQuery = useQuery(
    ['shareKeyValidation', { id: tenderId, key: shareKeyToValidate }],
    () =>
      validateShareKey({
        shareId: tenderId,
        shareKey: shareKeyToValidate as string,
      }),
    {
      enabled: !!shareKeyToValidate && !!tenderId,
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error) => {
        appLog('validateShareKey error', error);
        message.error(
          intl.formatMessage({ id: 'public-share-invalid-link' }) ||
            'This public link is invalid or has expired'
        );
      },
    }
  );

  useEffect(() => {
    if (!tenderId) return;

    // Invitations route is valid only when shareKey is present in URL.
    if (!shareKeyFromUrl) {
      setCurrentCachedShare({
        shareId: tenderId,
        shareKey: null,
      });
      return;
    }

    // If current context already matches exact shareId + shareKey from URL, keep it.
    if (
      currentCachedShare?.shareId === tenderId &&
      currentCachedShare.shareKey === shareKeyFromUrl
    ) {
      return;
    }

    // Try to resolve exact pair from cache first.
    const shareFromCache = cachedSharesContext.find(
      ({ shareId, shareKey }) =>
        shareId === tenderId && shareKey === shareKeyFromUrl
    );

    if (shareFromCache) {
      setCurrentCachedShare({
        shareId: tenderId,
        shareKey: shareFromCache.shareKey,
      });
      return;
    }

    // Fallback to exact pair from URL.
    setCurrentCachedShare({
      shareId: tenderId,
      shareKey: shareKeyFromUrl,
    });
  }, [
    tenderId,
    cachedSharesContext,
    shareKeyFromUrl,
    currentCachedShare,
    setCurrentCachedShare,
  ]);

  const handleAddNotListedCachedTender = (newShare: StoredSharedTender) => {
    const storedShares: StoredSharedTender[] = [...cachedSharesContext];

    const existingShareIndex = storedShares.findIndex(
      (currShare) =>
        currShare.shareId === newShare.shareId &&
        currShare.shareKey === newShare.shareKey
    );

    // Keep multiple invitations for the same shareId if shareKey is different.
    if (existingShareIndex !== -1) {
      return;
    }

    storedShares.push(newShare);

    setCachedSharesContext(storedShares);
    localStorage.setItem('shares', JSON.stringify(storedShares));
  };

  // Helper: remove current cached share from local storage & context
  const removeCurrentCachedShare = () => {
    if (!currentCachedShare?.shareId || currentCachedShare.shareKey == null) {
      return;
    }

    const updatedCachedShares = cachedSharesContext.filter(
      (share) =>
        !(
          share.shareId === currentCachedShare.shareId &&
          share.shareKey === currentCachedShare.shareKey
        )
    );

    // Update both the context and localStorage
    setCachedSharesContext(updatedCachedShares);
    localStorage.setItem('shares', JSON.stringify(updatedCachedShares));
  };

  const handleRemoveFromCache = () => {
    setRemoveModalVisible(true);
  };

  const {
    data: shareStatus,
    error: shareStatusError,
    isLoading: isShareStatusLoading,
  } = useQuery(
    ['shareStatus', { id: tenderId, key: shareKeyToValidate }],
    () =>
      getShareStatus({
        shareId: tenderId,
        shareKey: shareKeyToValidate as string,
      }),
    {
      enabled: !!tenderId && !!shareKeyToValidate,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // Primary-/Secondary Share Keys: `shareStatus` is GET /shares/{shareId},
  // one of the endpoints confirmed (per JIRA) to already return
  // key.share_key_is_primary/parent_inviter - used to show the tender
  // notice disclaimer + inviting Primary-Share-Key's contact info here,
  // same as MyTendersTabs.tsx does post-claim.
  const isSecondaryShareKey =
    (shareStatus as any)?.key?.share_key_is_primary === false;

  // Check share / registration / share-key states
  const isShareRemovedByState = useMemo(() => {
    if (!shareStatus) return false;

    const status = shareStatus as any;
    const statesToCheck: number[] = [];

    // Share state
    if (status.share_state != null) {
      statesToCheck.push(status.share_state);
    }

    // Registration state
    if (status.registration?.registration_state != null) {
      statesToCheck.push(status.registration.registration_state);
    }

    // Share-key state
    if (status.key?.share_key_state != null) {
      statesToCheck.push(status.key.share_key_state);
    }

    if (!statesToCheck.length) return false;

    // Any of Share / Registration / Share-key with state >= REMOVED (9)
    // should be treated as "key not valid" (removed / deleted / expired).
    return statesToCheck.some((state) => state >= DB_STATE.REMOVED);
  }, [shareStatus]);

  const shareStatusHttpCode: number | undefined =
    (shareStatusError as any)?.response?.status || undefined;

  const isShareNotFound = shareStatusHttpCode === 404; // Bad URL – share does not exist
  const isShareForbidden = shareStatusHttpCode === 403; // Key not valid / access forbidden
  const isShareInvalid = isShareForbidden || isShareRemovedByState;

  const coreShareTenderQuery = useQuery(
    [QueryKeys.sharedTender, { id: tenderId, key: shareKeyToValidate }],
    loadTenderShareCoreRegistration,
    {
      enabled:
        !!tenderId &&
        !!shareKeyToValidate &&
        !isShareStatusLoading &&
        !isShareNotFound &&
        !isShareInvalid,
      refetchOnWindowFocus: false,
      retry: false, // Don't retry on failure to prevent multiple redirects
      onSuccess: (shareTender) => {
        // At this point we know share is not 404 and not "key not valid"
        handleAddNotListedCachedTender({
          title: shareTender?.registration_title || shareTender.title,
          shareKey: shareKeyToValidate || null,
          shareId: tenderId!,
        });
      },
      onError: (error) => {
        // Log the error but keep the user on the page with a generic message
        console.error('Error loading tender share:', error);
        // Display an error message to the user
        message.error(
          intl.formatMessage({ id: 'tender-share-load-error' }) ||
            'Failed to load tender share'
        );
      },
    }
  );

  // --- Share-key validity flags from /share_keys/validate ---
  const shareKeyValidation = shareKeyValidationQuery.data as
    | {
        is_valid?: boolean;
        validity_status?: {
          is_key_active?: boolean;
          is_key_expired?: boolean;
          is_key_claimed?: boolean;
          is_key_rejected?: boolean;
        };
      }
    | undefined;

  const validityStatus = shareKeyValidation?.validity_status;

  const isKeyValid = shareKeyValidation?.is_valid ?? true;
  const isKeyClaimed = !!validityStatus?.is_key_claimed;
  const isKeyExpired = !!validityStatus?.is_key_expired;
  const isKeyRejected = !!validityStatus?.is_key_rejected;
  const isKeyActive =
    validityStatus?.is_key_active ?? shareKeyValidation?.is_valid ?? true;

  const isKeyClaimable =
    isKeyValid &&
    isKeyActive &&
    !isKeyExpired &&
    !isKeyClaimed &&
    !isKeyRejected;

  // Rejectable: key is valid/active, not expired, not rejected and NOT claimed
  const isKeyRejectable =
    isKeyValid &&
    isKeyActive &&
    !isKeyExpired &&
    !isKeyRejected &&
    !isKeyClaimed;

  // Notification header above registration
  const keyNotification = useMemo<{
    tone: 'error' | 'warning';
    message: string;
  } | null>(() => {
    if (!shareKeyToValidate || shareKeyValidationQuery.isLoading) return null;

    // 1) Key cannot be claimed (already claimed / expired / revoked / rejected)
    if (!isKeyClaimable) {
      if (isKeyClaimed) {
        return {
          tone: 'error',
          message:
            intl.formatMessage({
              id: 'cached-share-key-already-claimed',
              defaultMessage:
                'This invitation key has already been claimed by another user.',
            }) ||
            'This invitation key has already been claimed by another user.',
        };
      }

      if (isKeyExpired) {
        return {
          tone: 'error',
          message:
            intl.formatMessage({
              id: 'cached-share-key-deadline-passed',
              defaultMessage:
                'The deadline for claiming this key has passed. It can no longer be claimed.',
            }) ||
            'The deadline for claiming this key has passed. It can no longer be claimed.',
        };
      }

      if (!isKeyActive) {
        return {
          tone: 'error',
          message:
            intl.formatMessage({
              id: 'cached-share-key-access-revoked',
              defaultMessage:
                'Access via this key has been revoked. You can no longer claim it.',
            }) ||
            'Access via this key has been revoked. You can no longer claim it.',
        };
      }

      if (isKeyRejected) {
        return {
          tone: 'error',
          message:
            intl.formatMessage({
              id: 'cached-share-key-rejected',
              defaultMessage:
                'This key has been marked as rejected. You can no longer claim it.',
            }) ||
            'This key has been marked as rejected. You can no longer claim it.',
        };
      }
    }

    // 2) Soft warning: participation / share deadlines are close
    const data: any = coreShareTenderQuery.data;

    const participationDeadline = data?.participation_deadline
      ? new Date(data.participation_deadline)
      : null;
    const shareDeadline = data?.share_deadline
      ? new Date(data.share_deadline)
      : null;

    const now = Date.now();
    const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;

    if (
      participationDeadline &&
      participationDeadline.getTime() > now &&
      participationDeadline.getTime() - now < twoWeeksMs
    ) {
      const formatted = formatDate(participationDeadline, 'short');

      return {
        tone: 'warning',
        message:
          intl.formatMessage(
            {
              id: 'cached-share-participation-closing-soon',
              defaultMessage:
                'Access to participation will close soon {datetime}',
            },
            { datetime: formatted }
          ) || `Access to participation will close soon ${formatted}`,
      };
    }

    if (
      shareDeadline &&
      shareDeadline.getTime() > now &&
      shareDeadline.getTime() - now < twoWeeksMs
    ) {
      const formatted = formatDate(shareDeadline, 'short');

      return {
        tone: 'warning',
        message:
          intl.formatMessage(
            {
              id: 'cached-share-offer-deadline-close-soon',
              defaultMessage: 'Deadline for offers is close: {datetime}',
            },
            { datetime: formatted }
          ) || `Deadline for offers is close: ${formatted}`,
      };
    }

    return null;
  }, [
    coreShareTenderQuery.data,
    intl,
    isKeyActive,
    isKeyClaimable,
    isKeyClaimed,
    isKeyExpired,
    isKeyRejected,
    shareKeyToValidate,
    shareKeyValidationQuery.isLoading,
  ]);

  const [
    dbRejectTenderShare,
    { isLoading: isUpdateTenderStateLoading },
  ] = useMutation(claimSharedTender, {
    onSuccess: () => {
      queryCache.invalidateQueries([
        QueryKeys.sharedTenderDetails,
        { id: tenderId, shareKey: currentTenderKey },
      ]);
    },
    onError: (err) => {
      message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
    },
    onSettled: () => {
      setIsSharedTenderVisible(false);
    },
  });

  const [
    dbClaimTenderShare,
    { isLoading: isUpdateTenderLoading },
  ] = useMutation(claimSharedTender, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.sharedTenderDetails]);
      message.success(
        intl.formatMessage({ id: 'tender-share-update-success' })
      );

      // When the share is claimed, remove it from the local cached shares list
      if (currentCachedShare?.shareId && currentCachedShare?.shareKey) {
        const updatedCachedShares = cachedSharesContext.filter(
          (share) =>
            !(
              share.shareId === currentCachedShare.shareId &&
              share.shareKey === currentCachedShare.shareKey
            )
        );

        // Update both the context and localStorage
        setCachedSharesContext(updatedCachedShares);
        localStorage.setItem('shares', JSON.stringify(updatedCachedShares));
      }
    },
    onError: (err) => {
      message.error(intl.formatMessage({ id: 'tender-claim-error' }));
    },
  });

  const handleClaimTenderShared = async (shareId, shareKey) => {
    if (!shareId || !shareKey) return;

    await dbClaimTenderShare({ shareId, shareKey });
  };

  // Reject share key (with optional delete from cache)
  const [dbRejectShareKey, { isLoading: isRejectKeyLoading }] = useMutation(
    (vars: {
      shareId: string;
      shareKey: string;
      message?: string;
      deleteAfter?: boolean;
    }) =>
      rejectShareKey({
        shareId: vars.shareId,
        shareKey: vars.shareKey,
        message: vars.message,
      }),
    {
      onSuccess: (_data, vars) => {
        queryCache.invalidateQueries('shareKeyValidation');
        queryCache.invalidateQueries([QueryKeys.sharedTenderDetails]);

        message.success(
          intl.formatMessage({
            id: 'cached-share-reject-success',
            defaultMessage: 'The invitation key was rejected.',
          })
        );

        if (vars.deleteAfter) {
          removeCurrentCachedShare();
          history.push('/invitations');
        }
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'cached-share-reject-error',
            defaultMessage: 'Failed to reject the invitation key.',
          })
        );
      },
      onSettled: () => {
        setRejectModalVisible(false);
      },
    }
  );

  const handleRejectKey = (text: string, deleteAfter: boolean) => {
    if (!currentCachedShare?.shareId || !currentCachedShare.shareKey) return;

    dbRejectShareKey({
      shareId: currentCachedShare.shareId,
      shareKey: currentCachedShare.shareKey,
      message: text.trim() || undefined,
      deleteAfter,
    });
  };

  // Handle default selected tab key when opening a shared tender
  useEffect(() => {
    if (!currentTenderKey) return;

    const pathSegments = history.location.pathname.split('/').filter(Boolean);

    const directMapping = {
      details: 'details',
      documents: 'documents',
    };

    setActiveTab((prevActiveTab) => {
      const currentActiveTab = pathSegments.find((segment) => {
        return Object.values(directMapping).includes(segment);
      });

      if (!currentActiveTab)
        handleTabChange(
          currentFolderId ? directMapping.documents : directMapping.details
        );
      return currentActiveTab || prevActiveTab;
    });
  }, [history.location.pathname, currentTenderKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (key: string) => {
    resetSelectedForDetails();
    setActiveTab(key);

    // Keep base URL (either /invitations/:tenderId or /.../folders/...)
    // and only replace trailing /details or /documents.
    const baseUrl = url.split('?')[0].replace(/\/(details|documents)$/, '');
    history.replace(`${baseUrl}/${key}`);
  };

  // 1) Share key is required for invitations route.
  //    If there is no resolved key in either URL or current cached share,
  //    do not render the invite view.
  if (!hasShareKey) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <div style={{ width: '100%' }}>
          <Result
            status="error"
            title={intl.formatMessage({ id: 'public-share-invalid-link' })}
            subTitle={intl.formatMessage({
              id: 'public-share-invalid-link-info',
            })}
          />
        </div>
        <div
          css={styles.actionsContainer}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="primary" danger onClick={handleRemoveFromCache}>
            {intl.formatMessage({
              id: 'cached-share-action-remove',
              defaultMessage: 'Remove',
            })}
          </Button>
        </div>
      </div>
    );
  }

  // 2) Share / URL does not exist (404) -> "Not Found" view (TV1-194)
  if (isShareNotFound) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <div style={{ width: '100%' }}>
          <Result
            status="404"
            title={intl.formatMessage({ id: 'public-share-not-found' })}
            subTitle={intl.formatMessage({
              id: 'public-share-not-found-info',
            })}
          />
        </div>
        <div
          css={styles.actionsContainer}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="primary" danger onClick={handleRemoveFromCache}>
            {intl.formatMessage({
              id: 'cached-share-action-remove',
              defaultMessage: 'Remove',
            })}
          </Button>
        </div>
      </div>
    );
  }

  // 3) Key / share / registration / share-key in REMOVED/DELETED/EXPIRED state
  //    or backend returned 403 -> "Key Not Valid" view (TV1-194)
  if (isShareInvalid) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <div style={{ width: '100%' }}>
          <Result
            status="error"
            title={intl.formatMessage({ id: 'key-not-valid' })}
            subTitle={intl.formatMessage({
              id: 'key-not-valid-info',
            })}
          />
        </div>
        <div
          css={styles.actionsContainer}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="primary" danger onClick={handleRemoveFromCache}>
            {intl.formatMessage({
              id: 'cached-share-action-remove',
              defaultMessage: 'Remove',
            })}
          </Button>
        </div>
      </div>
    );
  }

  // 4) Fallback: something went wrong while loading core registration
  if (coreShareTenderQuery.isError) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <div style={{ width: '100%' }}>
          <Result
            status="error"
            title={intl.formatMessage({ id: 'public-share-invalid-link' })}
            subTitle={intl.formatMessage({
              id: 'public-share-invalid-link-info',
            })}
          />
        </div>
        <div
          css={styles.actionsContainer}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="primary" danger onClick={handleRemoveFromCache}>
            {intl.formatMessage({
              id: 'cached-share-action-remove',
              defaultMessage: 'Remove',
            })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
      }}>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        defaultActiveKey="details"
        style={{ marginTop: '-2px', width: '100%' }}>
        <TabPane
          tab={
            <div style={{ paddingLeft: '8px' }}>
              {intl.formatMessage({ id: 'tab-details' })}
            </div>
          }
          key="details">
          <Switch>
            <Route path={`${path}/details`}>
              <RightSideContentWrapper>
                {keyNotification && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: '8px 12px',
                      borderRadius: 4,
                      border:
                        keyNotification.tone === 'error'
                          ? '1px solid #ffbb96'
                          : '1px solid #ffe58f',
                      backgroundColor:
                        keyNotification.tone === 'error'
                          ? '#fff2e8'
                          : '#fffbe6',
                      color:
                        keyNotification.tone === 'error'
                          ? '#ad2102'
                          : '#ad6800',
                    }}>
                    {keyNotification.message}
                  </div>
                )}

                <TenderDetails
                  tenderData={coreShareTenderQuery.data}
                  tenderId={tenderId}
                  isShares={
                    currentCachedShare
                      ? {
                          shareId: currentCachedShare?.shareId ?? '',
                          shareKey: currentCachedShare?.shareKey ?? '',
                        }
                      : undefined
                  }
                  isValidShare={!isShareForbidden || !isShareInvalid}
                  secondaryKeyInfo={
                    isSecondaryShareKey
                      ? { parentInviter: shareStatus?.key?.parent_inviter }
                      : undefined
                  }
                />
              </RightSideContentWrapper>
            </Route>
          </Switch>
        </TabPane>
        {/* documents tab commented out */}
      </Tabs>
      <div
        css={styles.actionsContainer}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* 1. Remove */}
        <Tooltip
          title={intl.formatMessage({
            id: 'cached-share-action-remove-tooltip',
          })}>
          <Button onClick={handleRemoveFromCache}>
            {intl.formatMessage({
              id: 'cached-share-action-remove',
              defaultMessage: 'Remove',
            })}
          </Button>
        </Tooltip>

        {/* 2. Reject */}
        <Tooltip
          title={intl.formatMessage({
            id: 'cached-share-action-reject-tooltip',
          })}>
          <Button
            type="primary"
            danger
            onClick={() => setRejectModalVisible(true)}
            loading={isRejectKeyLoading}
            disabled={!isKeyRejectable}>
            {intl.formatMessage({
              id: 'cached-share-action-reject',
              defaultMessage: 'Reject',
            })}
          </Button>
        </Tooltip>

        {/* 3. Claim */}
        <Tooltip
          title={intl.formatMessage({
            id: 'cached-share-action-claim-tooltip',
          })}>
          <Button
            onClick={() => setTenderClaimModalVisible(true)}
            type="primary"
            style={{ backgroundColor: '#2cbd9b', borderColor: '#2cbd9b' }}
            loading={isUpdateTenderLoading}
            disabled={!isKeyClaimable}>
            {intl.formatMessage({ id: 'claim' })}
          </Button>
        </Tooltip>
      </div>

      <RejectShareKeyModal
        visible={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setFromRemoveFlow(false);
        }}
        isSubmitting={isRejectKeyLoading}
        hideSimpleReject={fromRemoveFlow}
        onReject={(msg) => handleRejectKey(msg, false)}
        onRejectAndDelete={(msg) => handleRejectKey(msg, true)}
      />

      <TenderClaimModal
        shareData={currentCachedShare || null}
        visible={tenderClaimModalVisible}
        onCancel={() => {
          setTenderClaimModalVisible(false);
        }}
        onDone={() => {
          setTenderClaimModalVisible(false);
        }}
      />

      <Modal
        visible={removeModalVisible}
        onCancel={() => setRemoveModalVisible(false)}
        footer={null}
        centered
        width={440}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} rev={''} />
            {intl.formatMessage({ id: 'cached-share-remove-from-list-title' })}
          </div>
        }>
        <div style={{ marginBottom: 24 }}>
          {intl.formatMessage({
            id: 'cached-share-remove-from-list-description',
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => setRemoveModalVisible(false)}>
            {intl.formatMessage({ id: 'close' })}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              removeCurrentCachedShare();
              history.push('/invitations');
            }}>
            {intl.formatMessage({ id: 'cached-share-remove-from-list-ok' })}
          </Button>
          <Button
            type="primary"
            danger
            disabled={!isKeyRejectable}
            onClick={() => {
              setFromRemoveFlow(true);
              setRemoveModalVisible(false);
              setRejectModalVisible(true);
            }}>
            {intl.formatMessage({ id: 'cached-share-reject-delete-modal-ok' })}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
